import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { MessageSquarePlus } from 'lucide-react'
import { api, errorMessage, fieldErrors } from '@/lib/api'
import { formatDateTime } from '@/lib/date'
import type { OrderEvent, PendingRemark } from '@/types'
import Modal from '@/components/ui/Modal'
import Button from '@/components/ui/Button'
import { Field, Textarea } from '@/components/ui/Field'
import { useToast } from '@/components/ui/toast'
import { invalidateOrderViews } from '@/components/order/lifecycle'

/** The order page carries every event; its pending remarks, newest first. */
export function remarksFromEvents(events: OrderEvent[] | undefined): PendingRemark[] {
  return (events ?? [])
    .filter((event) => event.type === 'pending_remark' && event.note)
    .map((event) => ({ remark: event.note as string, by: event.actor, at: event.at }))
    .reverse()
}

/**
 * Why a pending order is still held, laid out like the remark rows under a
 * pending order in the Excard portal the office already works: date, remark,
 * who wrote it. Newest first.
 */
export function PendingRemarkList({ remarks, className }: { remarks: PendingRemark[]; className?: string }) {
  if (remarks.length === 0) return <p className={className ?? 'text-xs text-muted'}>No remarks yet.</p>

  return (
    <div className={className}>
      <table className="w-full text-left text-xs">
        <thead className="text-2xs uppercase text-muted">
          <tr>
            <th className="py-1 pr-3 font-semibold">Date</th>
            <th className="py-1 pr-3 font-semibold">Remark</th>
            <th className="py-1 font-semibold">By</th>
          </tr>
        </thead>
        <tbody>
          {remarks.map((row, i) => (
            <tr key={`${row.at}-${i}`} className="border-t border-line align-top">
              <td className="whitespace-nowrap py-1 pr-3">{formatDateTime(row.at)}</td>
              <td className="py-1 pr-3 whitespace-pre-line">{row.remark}</td>
              <td className="whitespace-nowrap py-1">{row.by ?? '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** Add a remark to a pending order, with the earlier ones above the box. */
export function PendingRemarkModal({
  basePath,
  order,
  remarks,
  onClose,
}: {
  basePath: '/admin' | '/sales'
  /** The order being remarked; null keeps the modal shut. */
  order: { id: number; number: string } | null
  remarks: PendingRemark[]
  onClose: () => void
}) {
  const [text, setText] = useState('')
  const [error, setError] = useState<string | undefined>()
  const toast = useToast()
  const queryClient = useQueryClient()

  const close = () => {
    setText('')
    setError(undefined)
    onClose()
  }

  const save = useMutation({
    mutationFn: async () => (await api.post<{ message: string }>(`${basePath}/orders/${order?.id}/pending-remarks`, { remark: text })).data,
    onSuccess: (res) => {
      toast.success(res.message)
      invalidateOrderViews(queryClient)
      close()
    },
    onError: (err) => {
      const errors = fieldErrors(err)
      if (errors.remark?.[0]) setError(errors.remark[0])
      else toast.error('Could not add the remark', errorMessage(err))
    },
  })

  return (
    <Modal
      open={!!order}
      onClose={close}
      title={`Remark - ${order?.number ?? ''}`}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button loading={save.isPending} disabled={!text.trim()} onClick={() => save.mutate()}>
            <MessageSquarePlus className="size-4" /> Add remark
          </Button>
        </>
      }
    >
      <div className="grid gap-4">
        <PendingRemarkList remarks={remarks} />
        <Field label="Why is it still pending?" htmlFor="pending-remark" error={error}>
          <Textarea
            id="pending-remark"
            rows={3}
            maxLength={500}
            placeholder="e.g. logo not embed, waiting customer artwork"
            value={text}
            onChange={(e) => {
              setText(e.target.value)
              setError(undefined)
            }}
          />
        </Field>
      </div>
    </Modal>
  )
}
